/**
 * `<gh-claim-card>` - the claim, always in view (§AM step 4; owner, 2026-09-24: *"present and clearly
 * visible in the UI"*).
 *
 * Two shapes of one thing: `sidebar` sits where a signed-in account shows its email and Sign out (the
 * slot a guest has no use for, since they have no email and signing out would delete their work), and
 * `bar` is the phone's top bar, where the sidebar is a drawer nobody sees until they open it.
 *
 * ⚠ **"Leave without claiming" is here, and "Sign out" is not.** For a guest the cookie IS the account,
 * so the only honest version of signing out is the dialog that says leaving deletes the work.
 * ⚠ Mounted by `app.ts` only for a guest, so it renders unconditionally - a component that renders
 * nothing must not BE there (round 204), and a guest is the only reader it has.
 */
import { css, html, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { GhElement } from "./ghElement.ts";
import { buttonStyles, chromeStyles, focusStyles, resetStyles, tooltipStyles } from "./sharedStyles.ts";
import { t } from "../i18n.ts";
import { showClaimDialog } from "./claimDialog.ts";

@customElement("gh-claim-card")
export class GhClaimCard extends GhElement {
	@property() accessor variant: "sidebar" | "bar" = "sidebar";

	static styles = [
		resetStyles,
		chromeStyles,
		buttonStyles,
		tooltipStyles,
		focusStyles,
		css`
			:host {
				display: block;
			}
			.claim-card {
				margin: 14px 12px 0;
				padding: 12px;
				border: 1px solid var(--chrome-border);
				border-radius: var(--radius);
				background: var(--chrome-bg3);
				color: var(--chrome-text);
				display: flex;
				flex-direction: column;
				gap: 10px;
			}
			.claim-state {
				display: flex;
				align-items: center;
				gap: 8px;
				font-size: 0.8125rem;
				font-weight: 600;
			}
			.claim-state .tooltip-icon {
				margin-left: auto;
			}
			/* A live mark rather than a static one, for the reason the owner asked for this card at all:
			   the one thing on the screen that says the work is not safe yet. The ring is the accent at a
			   quarter, which every palette can carry. */
			.claim-dot {
				width: 8px;
				height: 8px;
				border-radius: 50%;
				flex: none;
				background: var(--accent);
				box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 25%, transparent);
			}
			.claim-card .btn-primary {
				width: 100%;
				justify-content: center;
			}
			.claim-leave {
				align-self: flex-start;
				padding: 0;
				border: 0;
				background: none;
				color: var(--chrome-text2);
				font: inherit;
				font-size: 0.75rem;
				text-decoration: underline;
				cursor: pointer;
			}
			.claim-bar {
				justify-self: end;
			}
		`
	];

	protected render(): TemplateResult {
		if (this.variant === "bar") {
			return html`<button type="button" class="btn btn-primary btn-small claim-bar" data-action="claim-open" @click=${() => showClaimDialog()}>
				${t("claim.buttonShort")}
			</button>`;
		}
		return html`<div class="claim-card">
			<p class="claim-state">
				<span class="claim-dot" aria-hidden="true"></span>${t("claim.cardState")}
				<span class="tooltip-icon" tabindex="0" data-tooltip=${t("claim.cardTip")}>?</span>
			</p>
			<button type="button" class="btn btn-primary" data-action="claim-open" @click=${() => showClaimDialog()}>${t("claim.button")}</button>
			<button type="button" class="claim-leave" data-action="claim-leave" @click=${() => showClaimDialog("leave")}>${t("claim.leave")}</button>
		</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"gh-claim-card": GhClaimCard;
	}
}
